import {
  GetTransactionsResponse,
  TransactionResponse,
} from "../types/transactions";
import {
  calculateDiscountedItemPrice,
  getDiscountedLinePrice,
  hasLineDiscount,
} from "./discountUtils";

/** Either transaction payload shape the receipt is printed from. */
export type ReceiptTransaction = GetTransactionsResponse | TransactionResponse;

export interface ReceiptLineItem {
  key: string;
  /** Item name, with a `× n` suffix when the quantity isn't 1. */
  label: string;
  /** Pre-tax line amount after line discounts (taxes/tips are totals rows). */
  amount: number;
}

const withQty = (name: string, qty: number) =>
  `${name}${qty !== 1 ? ` × ${qty}` : ""}`;

/** Full client name, or an em dash when unknown. */
export function clientName(row: Pick<ReceiptTransaction, "client">): string {
  const c = row.client;
  const name = [c?.first_name, c?.last_name].filter(Boolean).join(" ").trim();
  return name.length > 0 ? name : "—";
}

/**
 * The receipt's itemized rows — services, products, packages, then gift cards —
 * priced pre-tax and after line discounts so they foot to
 * `computeTransactionTotals().subtotalAfterRedemptions` (which includes gift
 * cards' face value).
 */
export function receiptLineItems(txn: ReceiptTransaction): ReceiptLineItem[] {
  const li = txn.line_items;

  const services = (li?.booked_services ?? []).map((service) => {
    const qty = service.quantity ?? 1;
    const amount = hasLineDiscount(service)
      ? calculateDiscountedItemPrice(service)
      : service.calc__cost_excl_tips_discounts;
    return {
      key: `svc-${service.id}`,
      label: withQty(service.name, qty),
      amount,
    };
  });

  const products = (li?.products ?? []).map((product) => {
    const amount = hasLineDiscount(product)
      ? calculateDiscountedItemPrice(product, product.quantity)
      : product.unit_price * product.quantity;
    return {
      key: `prod-${product.id}`,
      label: withQty(product.name, product.quantity),
      amount,
    };
  });

  const packages = (li?.service_packages ?? []).map((pkg) => {
    const qty = pkg.quantity ?? 1;
    // Foot to computeTransactionTotals: backend-authoritative line cost
    // (already ×quantity) when undiscounted, else the line (unit price
    // ×quantity) after its discounts.
    const amount = hasLineDiscount(pkg)
      ? getDiscountedLinePrice(pkg, pkg.unit_price * qty)
      : (pkg.calc__cost_excl_tips_discounts ?? pkg.unit_price * qty);
    return {
      key: `pkg-${pkg.line_item_id}`,
      label: withQty(pkg.name, qty),
      amount,
    };
  });

  const giftCards = (li?.gift_cards ?? []).map((gc) => ({
    key: `gift-${gc.line_item_id}`,
    label: gc.name,
    amount: gc.amount || 0,
  }));

  return [...services, ...products, ...packages, ...giftCards];
}

/**
 * The appointment's start (UTC API datetime) — the earliest booked service
 * start — or null for a sale with no booked services.
 */
export function appointmentStart(txn: ReceiptTransaction): string | null {
  const starts = (txn.line_items?.booked_services ?? [])
    .map((s) => s.start_dt)
    .filter((d): d is string => !!d)
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
  return starts[0] ?? null;
}
